import React from 'react';
import { useHistory } from 'react-router';
import { useForm } from 'react-hook-form';
import styled from 'styled-components';

import useAuthQuery from '@/hooks/auth/useAuthQuery';

import GlobalAlert from '@/components/GlobalAlert';
import Card from '@/components/Card/Card';
import FormInputComponent from '@/components/FormInputComponent';
import TextComponent from '@/components/TextComponent';
import ButtonComponent from '@/components/ButtonComponent';
import { LoginForm } from '@/apis/auth/interface';
import { KeyActionPairProps, onPressKeyboard } from '@/utils/form';
import { VERSION } from '@/constants/version';
import theme from '@/styles/theme';

const LoginPage = () => {
  const history = useHistory();
  const { loginMutation } = useAuthQuery();

  const { control, handleSubmit, errors } = useForm<LoginForm>({
    defaultValues: {
      username: '',
      password: ''
    }
  });

  const onSubmit = (loginForm: LoginForm) => {
    const { username, password } = loginForm;

    if (!username && !password) {
      return GlobalAlert.show({
        title: '로그인 정보를 입력해주세요.'
      });
    }

    loginMutation.mutate({
      username: username.trim(),
      password: password.trim()
    });
  };

  const goToSignupPage = () => {
    history.push('/signup');
  };

  const goToFindAccountPage = (type: string) => {
    history.push({ pathname: '/findAccount', state: type });
  };

  const loginKeyBoardAction: Array<KeyActionPairProps> = [
    {
      matchKey: 'Enter',
      action: handleSubmit(onSubmit)
    }
  ];

  return (
    <Wrapper>
      <LoginFormWrapper>
        <Card>
          <CardBody>
            <TextComponent fontWeight={700} fontSize={28}>
              로그인
            </TextComponent>
            <FormInputComponent
              keyName="username"
              control={control}
              placeholder={'아이디 입력'}
              defaultValue={''}
              error={errors.username}
              rules={{
                minLength: { value: 5, message: '아이디를 확인해주세요' }
              }}
            />
            <FormInputComponent
              keyName="password"
              type="password"
              control={control}
              placeholder={'비밀번호 입력'}
              defaultValue={''}
              error={errors.password}
              rules={{
                minLength: { value: 8, message: '비밀번호를 확인해주세요' }
              }}
              onKeyUp={e => onPressKeyboard(loginKeyBoardAction, e.key)}
            />
            <ButtonComponent
              bgColor={theme.DF_COLOR.PRIMARY500}
              borderColor={theme.DF_COLOR.PRIMARY500}
              textColor={theme.DF_COLOR.WHITE}
              fontWeight={700}
              fontSize={20}
              text={'로그인'}
              onClick={handleSubmit(onSubmit)}
              style={{ marginBottom: 15, width: '100%' }}
            />
            <ButtonComponent
              bgColor={theme.DF_COLOR.WHITE}
              borderColor={theme.DF_COLOR.PRIMARY500}
              textColor={theme.DF_COLOR.PRIMARY500}
              fontWeight={700}
              fontSize={20}
              text={'회원가입'}
              onClick={goToSignupPage}
              style={{ width: '100%' }}
            />

            <FindAccount>
              <FindAccountText onClick={() => goToFindAccountPage('id')}>
                아이디 찾기
              </FindAccountText>
              <FindAccountText>|</FindAccountText>
              <FindAccountText onClick={() => goToFindAccountPage('password')}>
                비밀번호 찾기
              </FindAccountText>
            </FindAccount>

            <Version>
              <TextComponent>Ver {VERSION}</TextComponent>
            </Version>
          </CardBody>
        </Card>
      </LoginFormWrapper>
    </Wrapper>
  );
};

export default LoginPage;

const Wrapper = styled.div`
  display: flex;
  height: 100%;
  align-items: center;
  justify-content: center;
`;

const LoginFormWrapper = styled.div`
  width: 55rem;
  position: relative;
  margin-top: 3rem;
`;

const CardBody = styled.div`
  padding: 4rem 7rem;

  > * {
    &:nth-child(1) {
      margin-bottom: 3rem;
    }
  }
`;

const FindAccount = styled.div`
  display: flex;
  flex-flow: row;
  justify-content: center;
  margin-top: 3rem;

  > * {
    &:nth-child(2) {
      margin: 0 5px;

      &:hover {
        cursor: default;
      }
    }
  }
`;

const FindAccountText = styled(TextComponent)`
  color: ${theme.DF_COLOR.GRAY600};

  &:hover {
    cursor: pointer;
    opacity: 0.5;
  }
`;

const Version = styled.div`
  position: absolute;
  right: 1rem;
  bottom: 1rem;

  * {
    color: ${theme.DF_COLOR.GRAY600};
    font-weight: 600;
  }
`;
